import * as React from 'react';
import {
  ACTION_CARD_CLICKED,
  CATEGORY_WIDGET_EXPERIENCE_CARD,
} from 'utils/analytics/eventTypesConstants';
import { Card, CardActionArea, CardContent } from 'ui';
import { CardDetails } from './CardDetails';
import { CardImage } from './CardImage';
import { CardTitle } from './CardTitle';
import { CurrencyContext, LocaleContext, consumer, core } from '@tock/coretypes';
import { IntlProvider } from 'react-intl';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Anchor from 'components/Anchor';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardRoot: {
      borderRadius: 3,
      border: `1px solid ${theme.colors.borderDefault}`,
      flexBasis: '100%',
      display: 'flex',
      overflow: 'visible',
    },
    contentRoot: {
      flex: '1 0 auto',
      padding: theme.spacing(2),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    cardActionAreaRoot: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'flex-start',
      '&:hover': {
        backgroundColor: theme.colors.bgDefault,
        boxShadow: '1px 1px 3px rgba(0, 0, 0, 0.05)', // these will become theme shadows when they are finalized
        textDecoration: 'none !important',
      },
      '&:focus .MuiCardActionArea-focusHighlight': {
        boxShadow: theme.boxShadows.focus,
      },
      '& h3': {
        transition: `${theme.transitions.duration.short}ms color ${theme.transitions.easing.easeInOut}`,
      },
      '&:hover h3': {
        color: theme.colors.fgPrimaryBase,
        '@media (hover: none)': {
          color: theme.colors.fgDefault,
        },
      },
      '&:focus h3': {
        color: theme.colors.fgPrimaryBase,
      },
      '&:active h3': {
        color: theme.colors.fgPrimaryBase,
      },
      '&:hover .MuiCardActionArea-focusHighlight': {
        opacity: 0,
      },
    },
  })
);

export interface Props {
  altText: string;
  business: consumer.OfferingBusiness;
  className?: string;
  date?: string;
  dominantColor: string;
  href: string;
  image: string;
  location: string;
  priceCents: consumer.IOfferedPriceSummary | undefined;
  schedule?: core.Schedule;
  subtitle: JSX.Element | string;
  title: string;
  widgetModifiers?: consumer.WidgetModifier[];
  index?: number;
  onClick?(): void;
}

export const ExperienceCard: React.FunctionComponent<Props> = ({
  altText,
  business,
  className,
  date,
  dominantColor,
  href,
  image,
  location,
  priceCents,
  schedule,
  subtitle,
  title,
  index,
  onClick,
}) => {
  const classes = useStyles();
  const onClickGA = () => {
    window.analytics.sendEvent(CATEGORY_WIDGET_EXPERIENCE_CARD, ACTION_CARD_CLICKED, title, index);
    onClick && onClick();
  };
   
  return (
    <IntlProvider locale={business.locale}>
      <LocaleContext.Provider value={business.locale}>
        <CurrencyContext.Provider value={business.currencyCode}>
          <Card className={className} classes={{ root: classes.cardRoot }} variant="outlined">
            <CardActionArea
              classes={{
                root: classes.cardActionAreaRoot,
              }}
              component={Anchor}
              role="link"
              href={href}
              disableRipple
              aria-haspopup={true}
              onClick={onClickGA}
            >
              <CardImage image={image} altText={altText} dominantColor={dominantColor} />
              <CardContent classes={{ root: classes.contentRoot }}>
                <CardTitle title={subtitle} subtitle={title} gutterBottom reverse />
                <CardDetails
                  date={date}
                  priceCents={priceCents}
                  location={location}
                  schedule={schedule}
                />
              </CardContent>
            </CardActionArea>
          </Card>
        </CurrencyContext.Provider>
      </LocaleContext.Provider>
    </IntlProvider>
  );
};
